import { useLoad } from "@allergy123/api-client/react";
import { Card, Status, Table, Td, Th } from "@allergy123/ui";
import { formatDate } from "../lib";

/** The five records, each on its own; none implies the next (training architecture §6). */
export function RecordsPage() {
  const data = useLoad((api) => api.learning.records());
  if (data.state === "error") return <Status kind="error" label={`Couldn't load your records (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const r = data.data;
  const empty = (what: string) => (
    <tr>
      <Td colSpan={4} className="text-ink-secondary">
        No {what} recorded yet.
      </Td>
    </tr>
  );
  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">Records</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">Everything recorded about your training, kept as separate facts: what you completed, how you did on checks, what an assessor observed, whether you're eligible for a recognition, and what was issued.</p>

      <div className="grid gap-6">
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Content completion</h2>
          <Table>
            <thead>
              <tr>
                <Th>Class</Th>
                <Th>Module</Th>
                <Th>Release</Th>
                <Th>Completed</Th>
              </tr>
            </thead>
            <tbody>
              {r.completions.length === 0
                ? empty("completions")
                : r.completions.map((c) => (
                    <tr key={c.classCode + c.completedAt}>
                      <Td>{c.classCode}</Td>
                      <Td>{c.moduleCode}</Td>
                      <Td>{c.releaseVersion}</Td>
                      <Td>{formatDate(c.completedAt)}</Td>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Knowledge results</h2>
          <Table>
            <thead>
              <tr>
                <Th>Check</Th>
                <Th>Attempt</Th>
                <Th>Score</Th>
                <Th>Result</Th>
              </tr>
            </thead>
            <tbody>
              {r.knowledgeResults.length === 0
                ? empty("knowledge results")
                : r.knowledgeResults.map((k) => (
                    <tr key={k.checkCode + k.attempt}>
                      <Td>{k.checkCode}</Td>
                      <Td>{k.attempt}</Td>
                      <Td>{k.scorePercent}%</Td>
                      <Td>{k.passed ? "Passed" : k.criticalItemsPassed ? "Not passed" : "Not passed — critical item missed"}</Td>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Practical evidence</h2>
          <p className="mb-3 text-small text-ink-secondary">Structured assessor records only — never patient information.</p>
          <Table>
            <thead>
              <tr>
                <Th>Activity</Th>
                <Th>Standard</Th>
                <Th>Result</Th>
                <Th>Performed</Th>
              </tr>
            </thead>
            <tbody>
              {r.practicalEvidence.length === 0
                ? empty("practical evidence")
                : r.practicalEvidence.map((p) => (
                    <tr key={p.activityCode + p.performedOn}>
                      <Td>{p.activityCode}</Td>
                      <Td>{p.standardVersion}</Td>
                      <Td>{p.result}</Td>
                      <Td>{formatDate(p.performedOn)}</Td>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Recognition eligibility</h2>
          <Table>
            <thead>
              <tr>
                <Th>Recognition</Th>
                <Th>Standard</Th>
                <Th>Eligible</Th>
                <Th>Evaluated</Th>
              </tr>
            </thead>
            <tbody>
              {r.eligibility.length === 0
                ? empty("eligibility evaluations")
                : r.eligibility.map((e) => (
                    <tr key={e.recognitionCode + e.evaluatedAt}>
                      <Td>{e.recognitionCode}</Td>
                      <Td>{e.standardVersion}</Td>
                      <Td>{e.eligible ? "Yes" : "Not yet"}</Td>
                      <Td>{formatDate(e.evaluatedAt)}</Td>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Recognitions issued</h2>
          <Table>
            <thead>
              <tr>
                <Th>Recognition</Th>
                <Th>Issuer</Th>
                <Th>Issued</Th>
                <Th>Expires</Th>
              </tr>
            </thead>
            <tbody>
              {r.recognitions.length === 0
                ? empty("recognitions")
                : r.recognitions.map((x) => (
                    <tr key={x.recognitionCode + x.issuedAt}>
                      <Td>{x.recognitionCode}</Td>
                      <Td>{x.issuer}</Td>
                      <Td>{formatDate(x.issuedAt)}</Td>
                      <Td>{x.expiresAt ? formatDate(x.expiresAt) : "—"}</Td>
                    </tr>
                  ))}
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}
